import { 
  signInWithPhoneNumber, 
  PhoneAuthProvider, 
  signInWithCredential,
  ConfirmationResult,
  RecaptchaVerifier 
} from 'firebase/auth';
import { auth, initializeRecaptcha } from '../lib/firebase/config';
import { doc, getDoc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { FirebasePhoneAuthChecker } from '../utils/firebasePhoneAuthChecker';

export class FirebasePhoneAuthService {
  private static recaptchaVerifier: RecaptchaVerifier | null = null;
  private static confirmationResult: ConfirmationResult | null = null;
  private static lastPhoneNumber: string | null = null;

  // Initialize reCAPTCHA verifier
  static initializeRecaptcha(): void {
    if (!this.recaptchaVerifier) {
      // Create a div for reCAPTCHA if it doesn't exist
      if (!document.getElementById('recaptcha-container')) {
        const recaptchaDiv = document.createElement('div');
        recaptchaDiv.id = 'recaptcha-container';
        recaptchaDiv.style.display = 'none'; // Hide the reCAPTCHA
        document.body.appendChild(recaptchaDiv);
      }

      this.recaptchaVerifier = initializeRecaptcha('recaptcha-container');
    }
  }

  // Send SMS verification code using Firebase Auth
  static async sendVerificationCode(phoneNumber: string): Promise<{ success: boolean; message: string }> {
    try {
      // Check Firebase configuration first
      const configCheck = await FirebasePhoneAuthChecker.checkConfiguration();
      if (!configCheck.isConfigured) {
        console.error('Firebase configuration issues:', configCheck.errors);
        throw new Error(`Firebase configuration error: ${configCheck.errors.join(', ')}`);
      }

      // Normalize phone number to E.164 format
      const normalizedPhone = this.normalizePhoneNumber(phoneNumber);
      console.log('📱 Sending SMS to normalized phone:', normalizedPhone);
      
      // Store phone number for fallback verification
      this.lastPhoneNumber = phoneNumber;
      
      // Initialize reCAPTCHA if not already done
      this.initializeRecaptcha();

      if (!this.recaptchaVerifier) {
        throw new Error('Failed to initialize reCAPTCHA');
      }

      console.log('🔐 reCAPTCHA initialized, attempting to send SMS...');
      console.log('🔧 Auth domain:', auth.app.options.authDomain);
      console.log('🔧 Project ID:', auth.app.options.projectId);

      // Send SMS using Firebase Auth
      this.confirmationResult = await signInWithPhoneNumber(
        auth, 
        normalizedPhone, 
        this.recaptchaVerifier
      );

      console.log('✅ SMS sent successfully to:', normalizedPhone);
      
      return {
        success: true,
        message: 'Verification code sent successfully'
      };
    } catch (error) {
      console.error('❌ Send SMS error:', error);
      console.error('Error details:', {
        code: (error as any)?.code,
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Log network-related info for 400 errors
      if (error instanceof Error && error.message.includes('400')) {
        console.error('🌐 Network error details:', {
          currentDomain: window.location.hostname,
          authDomain: auth.app.options.authDomain,
          projectId: auth.app.options.projectId
        });
      }
      
      // Reset reCAPTCHA on error
      if (this.recaptchaVerifier) {
        this.recaptchaVerifier.clear();
        this.recaptchaVerifier = null;
      }
      
      let errorMessage = 'Failed to send verification code';
      let setupInstructions = '';
      
      // Handle 400 errors specifically (most common in production)
      if (error instanceof Error && (error.message.includes('400') || (error as any)?.status === 400)) {
        errorMessage = `Domain Authorization Error: Your website domain (${window.location.hostname}) is not authorized in Firebase Console.`;
        setupInstructions = `\n\nTo fix:\n1. Go to Firebase Console\n2. Authentication → Settings → Authorized domains\n3. Add: ${window.location.hostname}\n4. Save and try again`;
      } else if (error instanceof Error) {
        const errorCode = (error as any)?.code;
        
        if (errorCode === 'auth/operation-not-allowed' || error.message.includes('operation-not-allowed')) {
          errorMessage = 'Phone authentication is not enabled in Firebase Console';
          setupInstructions = '\n\nTo enable:\n' + FirebasePhoneAuthChecker.getSetupInstructions().join('\n');
        } else if (errorCode === 'auth/invalid-phone-number' || error.message.includes('invalid-phone-number')) {
          errorMessage = `Invalid phone number format: ${phoneNumber}. Please use format like +1234567890`;
        } else if (errorCode === 'auth/too-many-requests' || error.message.includes('too-many-requests')) {
          errorMessage = 'Too many requests. Please wait 15 minutes and try again.';
        } else if (errorCode === 'auth/quota-exceeded' || error.message.includes('quota-exceeded')) {
          errorMessage = 'SMS quota exceeded. Check Firebase Console billing.';
        } else if (errorCode === 'auth/invalid-app-credential') {
          errorMessage = 'Firebase configuration error. Check your API keys.';
        } else if (error.message.includes('reCAPTCHA') || error.message.includes('recaptcha')) {
          errorMessage = `reCAPTCHA failed: Domain ${window.location.hostname} not authorized in Firebase Console.`;
          setupInstructions = `\n\nTo fix:\n1. Firebase Console → Authentication → Settings → Authorized domains\n2. Add: ${window.location.hostname}\n3. Save`;
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = `Network error: Likely domain authorization issue. Current domain: ${window.location.hostname}`;
          setupInstructions = `\n\nCheck Firebase Console authorized domains.`;
        }
      }
      
      throw new Error(errorMessage + setupInstructions);
    }
  }

  // Verify SMS code using Firebase Auth
  static async verifyCode(code: string): Promise<{ success: boolean; user: any }> {
    try {
      if (!this.confirmationResult) {
        throw new Error('No verification in progress. Please request a new code.');
      }

      // Verify the code with Firebase
      const result = await this.confirmationResult.confirm(code);
      const user = result.user;

      console.log('✅ Phone verification successful:', user.uid);

      // Try to get user data from Firestore, but don't fail if it doesn't work
      let userData;
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          // Update existing user's last login (but don't fail if this fails)
          userData = userDoc.data();
          try {
            await updateDoc(userDocRef, {
              lastLogin: Timestamp.now(),
              phoneVerified: true
            });
          } catch (updateError) {
            console.warn('Failed to update user login time:', updateError);
            // Continue anyway - the important part is authentication worked
          }
        } else {
          // Create new user document (but don't fail if this fails)
          userData = {
            uid: user.uid,
            phone: user.phoneNumber,
            phoneVerified: true,
            createdAt: Timestamp.now(),
            lastLogin: Timestamp.now(),
            status: 'active',
            roles: ['user']
          };
          try {
            await setDoc(userDocRef, userData);
          } catch (createError) {
            console.warn('Failed to create user document:', createError);
            // Continue anyway - the important part is authentication worked
          }
        }
      } catch (firestoreError) {
        console.warn('Firestore operation failed, using basic user data:', firestoreError);
        // If Firestore fails completely, create basic user data from Firebase Auth
        userData = {
          uid: user.uid,
          phone: user.phoneNumber,
          phoneVerified: true,
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
          status: 'active',
          roles: ['user']
        };
      }

      // Clear confirmation result
      this.confirmationResult = null;

      return {
        success: true,
        user: {
          uid: user.uid,
          phone: user.phoneNumber,
          email: userData.email || '',
          displayName: userData.displayName || userData.name || '',
          roles: userData.roles || ['user'],
          status: userData.status || 'active',
          storeId: userData.storeId,
          createdAt: userData.createdAt,
          lastLogin: Timestamp.now()
        }
      };
    } catch (error) {
      console.error('Verify code error:', error);
      
      let errorMessage = 'Invalid verification code';
      if (error instanceof Error) {
        if (error.message.includes('invalid-verification-code')) {
          errorMessage = 'Invalid verification code. Please try again.';
        } else if (error.message.includes('code-expired')) {
          errorMessage = 'Verification code has expired. Please request a new one.';
        }
      }
      
      throw new Error(errorMessage);
    }
  }

  // Normalize phone number to E.164 format
  private static normalizePhoneNumber(phone: string): string {
    // Remove all non-digits
    const digitsOnly = phone.replace(/\D/g, '');
    
    // If it's 10 digits, assume US and add country code
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }
    
    // If it's 11 digits and starts with 1, add + sign
    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    }
    
    // Otherwise return as-is with + if not present
    return digitsOnly.startsWith('+') ? phone : `+${digitsOnly}`;
  }

  // Reset the verification state
  static reset(): void {
    this.confirmationResult = null;
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
  }

  // Check if phone number exists in user profiles (for backwards compatibility)
  static async checkPhoneExists(phone: string): Promise<boolean> {
    try {
      // For Firebase Phone Auth, we don't need to pre-check if phone exists
      // Firebase will handle the authentication and we'll create/update user records
      // But we can still check for existing users if needed for business logic
      return true; // Allow any phone number to attempt verification
    } catch (error) {
      console.error('Check phone error:', error);
      return false;
    }
  }
}
